import type {
  RuleBasedErrorEntry,
  VariationSummaryItem,
} from "@/lib/api/proofreading";
import type { ProofreadingIssueStatus } from "@/types/proofreading";
import {
  buildIssueId,
  buildVariationLocationId,
} from "@/lib/utils/proofreadingTransform";
import {
  getOriginStartLine,
  getOriginStartWord,
  getVariationOriginStartLine,
  getVariationOriginStartWord,
  type RuleBasedErrorEntryStored,
  type VariationSummaryPositionStored,
} from "@/lib/utils/proofreadingStoredTypes";

type PreservedIssueStatus = Exclude<ProofreadingIssueStatus, "pending">;

function isPreservedStatus(
  status: ProofreadingIssueStatus | undefined,
): status is PreservedIssueStatus {
  return status === "applied" || status === "rejected";
}

function buildRuleBasedIdentityKey(entry: RuleBasedErrorEntryStored): string {
  return `rule:${entry.manuscript_id}:${getOriginStartLine(entry)}:${getOriginStartWord(entry)}:${entry.error_code}`;
}

function buildVariationIdentityKey(
  manuscriptId: string,
  position: VariationSummaryPositionStored,
  variationType: string,
  canonical: string,
  variantText: string,
): string {
  return `variation:${manuscriptId}:${getVariationOriginStartLine(position)}:${getVariationOriginStartWord(position)}:${variationType}:${canonical}:${variantText}`;
}

/**
 * 校正再実行前後の Issue を突き合わせ、applied / rejected を新 ID へ引き継ぐ。
 * origin 固定 ID 前提で突合する。
 */
export function mergeIssueStatusesAcrossRuns(params: {
  previousResults: RuleBasedErrorEntry[];
  previousVariationSummary: VariationSummaryItem[];
  previousIssueStatuses: Map<string, ProofreadingIssueStatus>;
  nextResults: RuleBasedErrorEntry[];
  nextVariationSummary: VariationSummaryItem[];
}): Map<string, ProofreadingIssueStatus> {
  const {
    previousResults,
    previousVariationSummary,
    previousIssueStatuses,
    nextResults,
    nextVariationSummary,
  } = params;

  const statusByIdentity = new Map<string, PreservedIssueStatus>();

  previousResults.forEach((entry) => {
    const stored = entry as RuleBasedErrorEntryStored;
    const issueId = buildIssueId(stored);
    const status = previousIssueStatuses.get(issueId);
    if (!isPreservedStatus(status)) return;

    statusByIdentity.set(buildRuleBasedIdentityKey(stored), status);
  });

  previousVariationSummary.forEach((item) => {
    item.variants.forEach((variant) => {
      (variant.position ?? []).forEach((pos) => {
        const stored = pos as VariationSummaryPositionStored;
        const locationId = buildVariationLocationId(item, pos, variant.text);
        const status = previousIssueStatuses.get(locationId);
        if (!isPreservedStatus(status)) return;

        statusByIdentity.set(
          buildVariationIdentityKey(
            pos.manuscript_id,
            stored,
            item.variation_type,
            item.canonical,
            variant.text,
          ),
          status,
        );
      });
    });
  });

  const merged = new Map<string, ProofreadingIssueStatus>();

  nextResults.forEach((entry) => {
    const stored = entry as RuleBasedErrorEntryStored;
    const identityKey = buildRuleBasedIdentityKey(stored);
    const status = statusByIdentity.get(identityKey);
    if (!status) return;
    merged.set(buildIssueId(stored), status);
  });

  nextVariationSummary.forEach((item) => {
    item.variants.forEach((variant) => {
      (variant.position ?? []).forEach((pos) => {
        const stored = pos as VariationSummaryPositionStored;
        const identityKey = buildVariationIdentityKey(
          pos.manuscript_id,
          stored,
          item.variation_type,
          item.canonical,
          variant.text,
        );
        const status = statusByIdentity.get(identityKey);
        if (!status) return;
        merged.set(buildVariationLocationId(item, pos, variant.text), status);
      });
    });
  });

  return merged;
}
